import React from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";

import Icon from "../Icon";

const StyledButton = styled.button`
  border: none;
  outline: none;
  width: ${props => buttonSizes[props.size]};
  padding: 0.75em 0 .65em;
  border-radius: 1.5em;
  font-size: 1em;
  letter-spacing: 1px;
  cursor: pointer;
  transition: 0.2s all ease;
  font-family: Avenir;
  font-display: swap;
  margin: ${props => props.margin};
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
  font-size : ${props => {
    return props.fontSize ? props.fontSize : "1.4rem";
  }};
  &:hover{
    background-color: #d11868;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
  }
  i{
    margin-right: 1em;
    color: inherit;
    font-weight: 900;
  }
  &:disabled{
    cursor: not-allowed;
    background-color: #e5e5e5;
    color: #9b9b9b;
  }

  ${props =>
    props.type === "solid" &&
    css`
      background: ${buttonThemes[props.theme].bgCol};
      color: ${buttonThemes[props.theme].color};
    `}

  ${props =>
    props.type === "outline" &&
    css`
      border: 2px solid ${buttonThemes[props.theme].bdCol};
      background: transparent;
      color: ${buttonThemes[props.theme].bdCol};
      padding: 0.55em 0 0.45em;
      &:disabled {
        border: solid 1px #9b9b9b;
        color: #9b9b9b;
      }
      &:hover {
        background: transparent;
      }
    `}

  ${props =>
    props.type === "text" &&
    css`
      background: transparent;
      color: ${buttonThemes[props.theme].bdCol};
      align-self: center;
      box-shadow: none;
      &:hover {
        background: transparent;
        box-shadow: none;
      }
      &:disabled {
        border: none;
        color: #9b9b9b;
      }
    `}

  ${props =>
    props.isDisabled &&
    css`
      pointer-events: none;
      background-color: #e5e5e5;
      color: #9b9b9b;
    `}

  ${props =>
    props.isLoading &&
    css`
      background: repeating-linear-gradient(
          -45deg,
          rgba(242, 25, 118, 0.8),
          rgba(242, 25, 118, 0.8) 25%,
          rgba(242, 25, 118, 0.5) 25%,
          rgba(242, 25, 118, 0.5) 50%,
          rgba(242, 25, 118, 0.8) 50%
        )
        top left fixed;
      background-size: 48px 48px;
      animation: animate-stripes 3s linear infinite;
      pointer-events: none;
      &:hover {
        background-color: initial;
      }
    `}

  @keyframes animate-stripes {
    0% {background-position: 0 0;} 100% {background-position: 96px 0;}
  }
`;

/**
 * One button to rule them all
 */
const Button = props => {
  const {label, onClick, icon} = props;
  return (
    <StyledButton {...props} onClick={onClick}>
      {icon && <Icon name={icon} />} {label}
    </StyledButton>
  );
};

const propTypes = {
  icon: PropTypes.string,
  /** Label of the button */
  isDisabled: PropTypes.bool,

  /** Function to be called on button click */
  isLoading: PropTypes.bool,

  /** Size of the button */
  label: PropTypes.string.isRequired,

  /** Theme of the button */
  margin: PropTypes.string,

  /** Type of the button */
  onClick: PropTypes.func.isRequired,

  /** If button is loading */
  size: PropTypes.oneOf(["small", "normal", "large", "medium"]),

  /** If button is disabled */
  theme: PropTypes.oneOf(["light", "primary", "gray"]),

  /** Margin around the button */
  type: PropTypes.oneOf(["solid", "outline", "text"])
};

const defaultProps = {
  theme: "primary",
  size: "small",
  type: "solid",
  margin: "1em 0",
  isLoading: false
};

const buttonSizes = {
  small: "10em",
  normal: "15em",
  medium: "20em",
  large: "90%"
};

const buttonThemes = {
  light: {
    color: "#FFFFFF",
    bgCol: "transparent",
    bdCol: "#FFFFFF"
  },
  primary: {
    color: "#FFFFFF",
    bgCol: "#F21976",
    bdCol: "#F21976"
  },
  gray: {
    color: "#FFFFFF",
    bgCol: "#4A4A4A",
    bdCol: "#4A4A4A"
  }
};

Button.propTypes = propTypes;
Button.defaultProps = defaultProps;

export default Button;
